import type { ZoneCrossing, ZoneFailedPush } from '@chukua/common';
import { Alert, Pagination, Tag } from 'antd';
import clsx from 'clsx';
import { LogIn } from 'lucide-react';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { Skeleton } from '../../../../../shared/components/Skeleton';
import { SkeletonRegion } from '../../../../../shared/components/SkeletonRegion';
import { CROSSING_KIND_LABELS } from '../../../../../shared/policies/crossing-labels';
import { ZONE_WATCH_STATUS_LABELS } from '../../../policies/zone-labels';
import { useZoneWatch } from '../hooks/use-zone-watch';

function CrossingRow({ crossing }: { crossing: ZoneCrossing }) {
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
      <Tag className="m-0" color={crossing.kind === 'enter' ? 'success' : 'default'}>
        {CROSSING_KIND_LABELS[crossing.kind]}
      </Tag>
      <span className="font-medium text-fg">{crossing.truckRegistration}</span>
      {crossing.tripNumber && <span className="text-muted">{crossing.tripNumber}</span>}
      <time className="ml-auto text-xs text-muted" dateTime={crossing.deviceTime}>
        {new Date(crossing.deviceTime).toLocaleString()}
      </time>
    </li>
  );
}

function FailedPushes({ pushes }: { pushes: ZoneFailedPush[] }) {
  if (pushes.length === 0) return <p className="text-sm text-muted">No failed pushes.</p>;
  return (
    <div className="flex flex-col gap-2">
      {pushes.map((push) => (
        <Alert
          key={push.integrationId}
          type="error"
          showIcon
          title={`${push.integrationName}: failed after ${push.attempts} ${push.attempts === 1 ? 'attempt' : 'attempts'}`}
          description={push.lastError ?? 'No reason was given.'}
        />
      ))}
    </div>
  );
}

function WatchSkeleton() {
  return (
    <SkeletonRegion label="Loading arrivals and departures" className="flex flex-col gap-3">
      <Skeleton className="h-4 w-40" />
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-full" />
    </SkeletonRegion>
  );
}

/** A watched zone's status, its recent arrivals and departures, and any vendor push that has failed (#447). Mount with `key={zoneId}`. */
export function ZoneWatchSection({ zoneId }: { zoneId: string }) {
  const { state, page, setPage, pageSize } = useZoneWatch(zoneId);

  return (
    <section aria-label="Watching" className="flex flex-col gap-4 border-t border-line pt-5">
      <h3 className="text-base font-semibold text-fg">Watching</h3>
      {state.status === 'loading' && <WatchSkeleton />}
      {state.status === 'error' && <Alert type="error" showIcon title="Could not load the arrivals and departures. Try again in a moment." />}
      {state.status === 'ready' && (
        <>
          <p className="text-sm text-fg">{ZONE_WATCH_STATUS_LABELS[state.watch.status]}</p>
          <div>
            <h4 className="text-sm font-medium text-fg">Recent arrivals and departures</h4>
            {state.crossings.total === 0 ? (
              <EmptyState icon={LogIn} title="No arrivals yet" description="A truck entering or leaving this zone will show here." />
            ) : (
              <ul className={clsx('divide-y divide-line', state.isPaging && 'opacity-60')}>
                {state.crossings.items.map((crossing) => (
                  <CrossingRow key={crossing.id} crossing={crossing} />
                ))}
              </ul>
            )}
            {state.crossings.total > pageSize && (
              <Pagination
                className="mt-2"
                size="small"
                simple
                current={page}
                pageSize={pageSize}
                total={state.crossings.total}
                onChange={setPage}
                showSizeChanger={false}
              />
            )}
          </div>
          <div>
            <h4 className="mb-2 text-sm font-medium text-fg">Vendor push</h4>
            <FailedPushes pushes={state.watch.failedPushes} />
          </div>
        </>
      )}
    </section>
  );
}
